import { useMemo, useState } from "react";
import { IconChevronLeft, IconChevronRight } from "../icons";
import { tomDoStatus } from "./EventoPecas";

/**
 * CALENDÁRIO DOS EVENTOS — a mesma lista, vista por mês.
 *
 * Feito à mão, como o calendário do agendamento de entrevistas: o sistema não
 * usa biblioteca de calendário, e trazer uma só para desenhar uma grade de 7
 * colunas custaria mais (peso, tema, tradução) do que resolve.
 *
 * Evento de VÁRIOS DIAS aparece em todos os dias do intervalo — quem olha o mês
 * precisa ver que a semana inteira está ocupada, não só o dia da abertura.
 */

export interface EventoDoCalendario {
  id: number;
  nome: string;
  data: string | null;
  data_fim?: string | null;
  status?: string | null;
  hora_inicio?: string | null;
  dia_inteiro?: boolean;
}

const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

/** Data → "AAAA-MM-DD" pelo calendário LOCAL (toISOString jogaria o dia para trás). */
function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function EventosCalendario({
  eventos,
  onAbrir,
}: {
  eventos: EventoDoCalendario[];
  onAbrir: (id: number) => void;
}) {
  const hoje = iso(new Date());
  const [mes, setMes] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  /**
   * Índice dia → eventos. Um evento de 02 a 04 entra nos três dias; sem isso,
   * o calendário mostraria a semana livre com um evento acontecendo nela.
   */
  const porDia = useMemo(() => {
    const mapa = new Map<string, EventoDoCalendario[]>();
    for (const e of eventos) {
      if (!e.data) continue;
      const inicio = new Date(`${e.data}T00:00:00`);
      const fim = e.data_fim ? new Date(`${e.data_fim}T00:00:00`) : inicio;
      if (Number.isNaN(inicio.getTime())) continue;
      // Trava de segurança: intervalo absurdo (data digitada errada) não pode
      // gerar um laço infinito de anos.
      for (let d = new Date(inicio), i = 0; d <= fim && i < 400; d.setDate(d.getDate() + 1), i++) {
        const chave = iso(d);
        if (!mapa.has(chave)) mapa.set(chave, []);
        mapa.get(chave)!.push(e);
      }
    }
    return mapa;
  }, [eventos]);

  const celulas = useMemo(() => {
    const ano = mes.getFullYear();
    const m = mes.getMonth();
    const vazios = new Date(ano, m, 1).getDay();
    const total = new Date(ano, m + 1, 0).getDate();
    const lista: (string | null)[] = Array(vazios).fill(null);
    for (let d = 1; d <= total; d++) lista.push(iso(new Date(ano, m, d)));
    // Completa a última semana para a grade não ficar "quebrada" embaixo.
    while (lista.length % 7 !== 0) lista.push(null);
    return lista;
  }, [mes]);

  const noMes = useMemo(
    () =>
      eventos.filter((e) => {
        if (!e.data) return false;
        const chave = `${mes.getFullYear()}-${String(mes.getMonth() + 1).padStart(2, "0")}`;
        return e.data.startsWith(chave) || (e.data_fim ?? "").startsWith(chave);
      }).length,
    [eventos, mes],
  );

  return (
    <div className="evc">
      <div className="evc-topo">
        <button
          type="button"
          className="icon-btn"
          title="Mês anterior"
          onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))}
        >
          <IconChevronLeft size={17} />
        </button>
        <strong>
          {MESES[mes.getMonth()]} de {mes.getFullYear()}
        </strong>
        <button
          type="button"
          className="icon-btn"
          title="Próximo mês"
          onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))}
        >
          <IconChevronRight size={17} />
        </button>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => {
            const d = new Date();
            setMes(new Date(d.getFullYear(), d.getMonth(), 1));
          }}
        >
          Hoje
        </button>
        <span className="badge">{noMes} evento(s) no mês</span>
      </div>

      <div className="evc-semana">
        {DIAS_SEMANA.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>

      <div className="evc-grade">
        {celulas.map((dia, i) =>
          dia === null ? (
            <div key={`v${i}`} className="evc-dia evc-dia-vazio" />
          ) : (
            <div key={dia} className={`evc-dia ${dia === hoje ? "hoje" : ""}`}>
              <span className="evc-numero">{Number(dia.slice(-2))}</span>
              <div className="evc-eventos">
                {(porDia.get(dia) ?? []).map((e) => (
                  <button
                    key={`${e.id}-${dia}`}
                    type="button"
                    className={`evc-evento ${tomDoStatus(e.status)}`}
                    title={`${e.nome}${e.hora_inicio ? ` · ${e.hora_inicio}` : ""} — abrir`}
                    onClick={() => onAbrir(e.id)}
                  >
                    {!e.dia_inteiro && e.hora_inicio && <small>{e.hora_inicio}</small>}
                    <span>{e.nome}</span>
                  </button>
                ))}
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
}
